import {
	ClientTypeEnum,
	EditClientFormData,
	SocietyClientEditFormData,
	UserClientEditFormData,
} from "@dune/types";
import {
	Button,
	ControlledInput,
	ControlledPhoneInput,
	Dialog,
	DialogContent,
	DialogFooter,
	DialogHeader,
	DialogTitle,
	RootDialogProps,
} from "@dune/ui";
import { ClientDetailItem } from "@dune/vars";
import { useEffect } from "react";
import { FormProvider, useForm } from "react-hook-form";
import { toast } from "sonner";
import { useUpdateClient } from "../../../../../api/mutations/clients.mutations";
import { useGetSocietyInfoFromSirenNumber } from "../../../../../api/queries/siren.queries";
import AddressFormFields from "../../../../../component/shared/AddressFormFields";
import { useQueryClient } from "@tanstack/react-query";
import { clientsQueryKeys } from "../../../../../api/query-keys/clients.query-keys";

export const EditClientDialog = (
	props: RootDialogProps & { client: ClientDetailItem },
) => {
	const { client } = props;
	const isUser = client.clientType === ClientTypeEnum.USER;
	const queryClient = useQueryClient();

	const methods = useForm<EditClientFormData>({
		defaultValues: getInitialValues(client),
	});

	const {
		control,
		handleSubmit,
		setValue,
		getValues,
		watch,
		reset,
		formState: { isSubmitting },
	} = methods;
	const { mutate: updateClient } = useUpdateClient();

	const formData = watch();
	const sirenValue = !isUser && "siren" in formData ? formData.siren : "";
	const { data: sirenData, isSuccess: isSirenSuccess } =
		useGetSocietyInfoFromSirenNumber(sirenValue || "", false);

	useEffect(() => {
		if (!isUser && isSirenSuccess && sirenData) {
			const current = getValues("denomination");
			if (!current?.trim())
				setValue("denomination", sirenData.denomination || "");
		}
	}, [isSirenSuccess, sirenData, setValue, isUser, getValues]);

	useEffect(() => {
		if (client) {
			reset(getInitialValues(client));
		}
	}, [client, reset]);

	const onSubmit = (data: EditClientFormData) => {
		if (isUser && "firstName" in data) {
			const clientData = {
				type: ClientTypeEnum.USER,
				firstName: data.firstName,
				lastName: data.lastName,
				...(data.email?.trim() && { email: data.email }),
				...(data.mobilePhone && { mobilePhone: data.mobilePhone }),
				...(data.address && {
					address: {
						streetNumber: data.address.streetNumber || "",
						streetName: data.address.streetName || "",
						postalCode: data.address.postalCode || "",
						city: data.address.city || "",
					},
				}),
			};

			updateClient(
				{
					clientId: String(client.id),
					clientData,
				},
				{
					onSuccess: () => {
						queryClient.invalidateQueries({
							queryKey: clientsQueryKeys.detail(
								String(client.id),
								client.clientType || undefined,
							),
						});
						queryClient.invalidateQueries({
							queryKey: clientsQueryKeys.details(),
						});
						toast.success("Informations client mises à jour");
						queryClient.refetchQueries({
							queryKey: clientsQueryKeys.detail(
								String(client.id),
								client.clientType ?? undefined,
							),
						});
						props.onOpenChange?.(false);
					},
					onError: (error: Error) => {
						toast.error("Erreur lors de la mise à jour");
						console.error(error);
					},
				},
			);
		} else if (!isUser && "siren" in data) {
			const society = client.society;
			const contactUser =
				society?.syndicUserAccessToPlatform ?? society?.users?.[0];
			const contactUserId = contactUser?.id;

			const clientData = {
				type: ClientTypeEnum.SOCIETY,
				siren: data.siren,
				denomination: data.denomination || "",
				...(data.address && {
					address: {
						streetNumber: data.address.streetNumber || "",
						streetName: data.address.streetName || "",
						postalCode: data.address.postalCode || "",
						city: data.address.city || "",
					},
				}),
				...(data.contactInfo && {
					contactInfo: {
						...(contactUserId != null && { userId: contactUserId }),
						firstName: data.contactInfo.firstName || "",
						lastName: data.contactInfo.lastName || "",
						...(data.contactInfo.email?.trim() && {
							email: data.contactInfo.email,
						}),
						...(data.contactInfo.mobilePhone && {
							mobilePhone: data.contactInfo.mobilePhone,
						}),
						...(data.contactInfo.address && {
							address: {
								streetNumber: data.contactInfo.address.streetNumber || "",
								streetName: data.contactInfo.address.streetName || "",
								postalCode: data.contactInfo.address.postalCode || "",
								city: data.contactInfo.address.city || "",
							},
						}),
					},
				}),
			};
			updateClient(
				{
					clientId: String(client.id),
					clientData,
				},
				{
					onSuccess: () => {
						queryClient.invalidateQueries({
							queryKey: clientsQueryKeys.detail(
								String(client.id),
								client.clientType || undefined,
							),
						});
						queryClient.invalidateQueries({
							queryKey: clientsQueryKeys.details(),
						});
						toast.success("Informations client mises à jour");
						queryClient.refetchQueries({
							queryKey: clientsQueryKeys.detail(
								String(client.id),
								client.clientType ?? undefined,
							),
						});
						props.onOpenChange?.(false);
					},
					onError: (error: Error) => {
						toast.error("Erreur lors de la mise à jour");
						console.error(error);
					},
				},
			);
		}
	};

	return (
		<Dialog {...props}>
			<DialogContent className="max-w-6xl max-h-[90vh] overflow-y-auto">
				<DialogHeader>
					<DialogTitle>
						Modifier les informations {isUser ? "client" : "société"}
					</DialogTitle>
				</DialogHeader>

				<FormProvider {...methods}>
					<form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
						{isUser ? (
							<div className="space-y-6">
								<div>
									<h3 className="text-lg font-medium mb-4">
										Informations personnelles
									</h3>
									<div className="grid grid-cols-2 gap-4">
										<ControlledInput
											control={control}
											name="firstName"
											label="Prénom"
											required
										/>
										<ControlledInput
											control={control}
											name="lastName"
											label="Nom"
											required
										/>
									</div>
									<div className="grid grid-cols-2 gap-4 mt-4">
										<ControlledInput
											control={control}
											name="email"
											label="Email"
											type="email"
										/>
										<ControlledPhoneInput
											control={control}
											name="mobilePhone"
											label="Téléphone mobile"
										/>
									</div>
								</div>

								<div>
									<h3 className="text-lg font-medium mb-4">Adresse</h3>
									<AddressFormFields fieldPrefix="address" showBorder={false} />
								</div>
							</div>
						) : (
							<div className="space-y-6">
								<div className="grid grid-cols-1 gap-6 md:grid-cols-[1fr_auto_1fr]">
									<div className="space-y-6">
										<div>
											<h3 className="text-lg font-medium mb-4">
												Informations société
											</h3>
											<div className="grid grid-cols-2 gap-4">
												<ControlledInput
													control={control}
													name="siren"
													label="SIREN"
													placeholder="9 chiffres"
													maxLength={9}
													required
												/>
												<ControlledInput
													control={control}
													name="denomination"
													label="Dénomination"
													placeholder="Raison sociale"
												/>
											</div>
											{isSirenSuccess && sirenData && (
												<div className="mt-2 text-sm text-green-600">
													✓ Entreprise trouvée: {sirenData.denomination}
												</div>
											)}
										</div>

										<div>
											<h3 className="text-lg font-medium mb-4">
												Adresse société
											</h3>
											<AddressFormFields
												fieldPrefix="address"
												showBorder={false}
											/>
										</div>
									</div>

									<div className="hidden md:block w-px bg-gray-200" />

									<div className="space-y-6">
										<div>
											<h3 className="text-lg font-medium mb-4">
												Informations contact
											</h3>
											<div className="grid grid-cols-2 gap-4">
												<ControlledInput
													control={control}
													name="contactInfo.firstName"
													label="Prénom"
													required
												/>
												<ControlledInput
													control={control}
													name="contactInfo.lastName"
													label="Nom"
													required
												/>
											</div>
											<div className="grid grid-cols-2 gap-4 mt-4">
												<ControlledInput
													control={control}
													name="contactInfo.email"
													label="Email"
													type="email"
												/>
												<ControlledPhoneInput
													control={control}
													name="contactInfo.mobilePhone"
													label="Téléphone mobile"
												/>
											</div>
										</div>

										<div>
											<h3 className="text-lg font-medium mb-4">
												Adresse contact
											</h3>
											<AddressFormFields
												fieldPrefix="contactInfo.address"
												showBorder={false}
											/>
										</div>
									</div>
								</div>
							</div>
						)}

						<DialogFooter>
							<Button
								type="button"
								variant="outline"
								onClick={() => props.onOpenChange?.(false)}
								disabled={isSubmitting}
							>
								Annuler
							</Button>
							<Button
								type="submit"
								isLoading={isSubmitting}
								disabled={isSubmitting}
							>
								Enregistrer
							</Button>
						</DialogFooter>
					</form>
				</FormProvider>
			</DialogContent>
		</Dialog>
	);
};

function getInitialValues(
	client: ClientDetailItem,
): EditClientFormData {
	if (client.user) {
		const userFormData: UserClientEditFormData = {
			firstName: client.user.firstname || "",
			lastName: client.user.lastname || "",
			email: client.user.email || "",
			mobilePhone: client.user.mobilePhone || "",
			address: client.user.address
				? {
						streetNumber: client.user.address.streetNumber || "",
						streetName: client.user.address.streetName || "",
						postalCode: client.user.address.postalCode || "",
						city: client.user.address.city || "",
					}
				: undefined,
		};
		return userFormData;
	}

	const society = client.society;
	const contactInfo =
		society?.syndicUserAccessToPlatform ?? society?.users?.[0];
	const societyFormData: SocietyClientEditFormData = {
		siren: society?.siren || "",
		denomination: society?.denomination || "",
		address: society?.headOfficeAddress
			? {
					streetNumber: society.headOfficeAddress.streetNumber || "",
					streetName: society.headOfficeAddress.streetName || "",
					postalCode: society.headOfficeAddress.postalCode || "",
					city: society.headOfficeAddress.city || "",
				}
			: undefined,
		contactInfo: contactInfo
			? {
					firstName: contactInfo.firstname || "",
					lastName: contactInfo.lastname || "",
					email: contactInfo.email || "",
					mobilePhone: contactInfo.mobilePhone || "",
					address: contactInfo.address
						? {
								streetNumber: contactInfo.address.streetNumber || "",
								streetName: contactInfo.address.streetName || "",
								postalCode: contactInfo.address.postalCode || "",
								city: contactInfo.address.city || "",
							}
						: undefined,
				}
			: undefined,
	};
	return societyFormData;
}
